import React from "react";
import { FaFileAlt } from "react-icons/fa";

const ProjectDetailsDialog = ({ isOpen, onClose, setIsAddUpdateDone }) => {
    if (!isOpen) return null;

    //   Dummy data for demo
    const projectInfo = {
        name: "Technoland Automation",
        tenderId: "TN-2025-001",
        createdDate: "2025-10-29",
        clientName: "ABC Pvt. Ltd.",
        location: "Mumbai, India",
        department: "IT & Automation",
        division: "Technology",
        objective:
            "Technoland aims to streamline enterprise operations using AI-driven automation solutions.",
    };

    const sow =
        "The scope of work includes developing a centralized automation dashboard, integrating IoT sensors, and implementing predictive maintenance modules.";

    const team = [
        { role: "Project Manager", name: "John Doe", contact: "9876543210", remarks: "Lead PM" },
        { role: "Site Engineer", name: "Sarah Lee", contact: "9822334455", remarks: "Main site engineer" },
        { role: "Site Incharge", name: "Raj Patel", contact: "9988776655", remarks: "Overseeing setup" },
        { role: "Supervisor", name: "Amit Shah", contact: "9877665544", remarks: "Shift supervisor" },
        { role: "Vendor / Contractor", name: "XYZ Contractors", contact: "9123456789", remarks: "Electrical work" },
    ];

    const timeline = {
        startDate: "2025-10-10",
        endDate: "2026-03-15",
        duration: "5 Months",
        status: "In Progress",
    };

    const media = [
        { type: "Image", channels: "Social Media", duration: "Weekly", remarks: "Promotional posts" },
        { type: "Video", channels: "YouTube", duration: "Monthly", remarks: "Project updates" },
    ];

    const budget = {
        total: "$500,000",
        breakdown: "Development: $300K, Marketing: $100K, Logistics: $100K",
        approvalStatus: "Approved",
        remarks: "Within allocated budget",
    };

    const payments = [
        {
            terms: "50% Advance, 50% After Completion",
            cycle: "Milestone-based",
            summary: "3 payment milestones planned",
            remarks: "Pending final milestone",
        },
        {
            terms: "25% on Material Delivery",
            cycle: "On-site progress based",
            summary: "4 payment milestones planned",
            remarks: "2nd milestone in progress",
        },
    ];


    const bank = {
        name: "HDFC Bank",
        branch: "Andheri East",
        accNo: "XXXX-XXXX-1234",
        ifsc: "HDFC0001234",
        gst: "27ABCDE1234F1Z9",
        pan: "ABCDE1234F",
        method: "NEFT / RTGS",
        currency: "INR",
    };

    const client = {
        person: "Mr. Ravi Kumar",
        designation: "Client Manager",
        email: "ravi.kumar@abc.com",
        phone: "+91 9876543210",
        address: "123, Industrial Estate, Mumbai, India",
    };

    const progress = [
        { date: "2025-11-01", completed: "Base framework setup", progress: "25%", issues: "None" },
        { date: "2025-12-15", completed: "Module integration", progress: "60%", issues: "Minor API delays" },
    ];

    const milestones = [
        { milestone: "Design Phase", expected: "2025-11-30", actual: "2025-12-02", status: "Completed", remarks: "Slight delay" },
        { milestone: "Development Phase", expected: "2026-02-15", actual: "-", status: "Ongoing", remarks: "On schedule" },
    ];

    const notes =
        "All deliverables to be reviewed weekly. Attachments include design documents, approval letters, and test reports.";

    const handleSaveUserDetails = async () => {
        console.log("Project details saved");
        setIsAddUpdateDone(true);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeInUp flex flex-col text-gray-800">
                {/* Header */}
                <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <h2 className="text-lg font-semibold">Project Information</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-800 transition-all text-lg"
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-6 text-sm">
                    {/* 1. Project Overview */}
                    <Section title="1. Project Overview">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {Object.entries(projectInfo)
                                .filter(([k]) => k !== "objective")
                                .map(([k, v]) => (
                                    <InfoItem key={k} label={toTitle(k)} value={v} />
                                ))}
                        </div>
                        <div className="mt-4">
                            <p className="text-gray-500 text-xs mb-1">Project Objective / Summary</p>
                            <p className="font-medium text-gray-700 leading-relaxed">{projectInfo.objective}</p>
                        </div>
                    </Section>

                    {/* 2. Scope of Work */}
                    <Section title="2. Scope of Work (SOW)">
                        <p className="text-gray-700 leading-relaxed">{sow}</p>
                    </Section>

                    {/* 3. Team & Site Management */}
                    <Section title="3. Team & Site Management">
                        <Table
                            headers={["Role", "Name", "Contact", "Remarks"]}
                            data={team}
                        />
                    </Section>

                    {/* 4. Timeline */}
                    <Section title="4. Timeline">
                        <div className="grid sm:grid-cols-4 gap-4">
                            {Object.entries(timeline).map(([k, v]) => (
                                <InfoItem key={k} label={toTitle(k)} value={v} />
                            ))}
                        </div>
                    </Section>

                    {/* 5. Media Details */}
                    <Section title="5. Media Details">
                        <Table
                            headers={["Media Type", "Channels", "Duration / Frequency", "Remarks"]}
                            data={media}
                        />
                    </Section>

                    {/* 6. Budget Information */}
                    <Section title="6. Budget Information">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <InfoItem label="Total Budget" value={budget.total} />
                            <InfoItem label="Cost To Client" value={budget.total} />
                            <InfoItem label="Cost To Technoland" value={budget.total} />
                            <InfoItem label="Approval Status" value={budget.approvalStatus} />
                            <InfoItem label="Cost Breakdown" value={budget.breakdown} />
                            <InfoItem label="Remarks" value={budget.remarks} />
                        </div>
                    </Section>

                    {/* 7. Payment Cycle & Report */}
                    <Section title="7. Payment Cycle & Report">
                        <PaymentFlow />
                    </Section>


                    {/* 8. Bank & Financial Details */}
                    <Section title="8. Bank & Financial Details">
                        <div className="grid sm:grid-cols-2 gap-4">
                            {Object.entries(bank).map(([k, v]) => (
                                <InfoItem key={k} label={toTitle(k)} value={v} />
                            ))}
                        </div>
                    </Section>

                    {/* 9. Client Information */}
                    <Section title="9. Client Information">
                        <div className="grid sm:grid-cols-2 gap-4">
                            {Object.entries(client).map(([k, v]) => (
                                <InfoItem key={k} label={toTitle(k)} value={v} />
                            ))}
                        </div>
                    </Section>

                    {/* 10. Progress Summary */}
                    {/* <Section title="10. Progress Summary">
                        <Table
                            headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
                            data={progress}
                        />
                    </Section> */}
                    {/* 10. Progress Summary with Tabs */}
                    <Section title="10. Progress Summary">
                        <ProgressTabs />
                    </Section>


                    {/* 11. Milestones & Deliverables */}
                    <Section title="11. Milestones & Deliverables">
                        <Table
                            headers={["Milestone", "Expected Completion", "Actual Completion", "Status", "Remarks"]}
                            data={milestones}
                        />
                    </Section>

                    {/* 12. Additional Notes / Attachments */}
                    <Section title="12. Additional Notes / Attachments">
                        <p className="text-gray-700 leading-relaxed">{notes}</p>
                    </Section>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 sticky bottom-0">
                    <button
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium transition-all"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        className="bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] hover:from-[#135EA0] hover:to-[#00B8D4] text-white px-5 py-2 rounded-md text-sm font-medium shadow-md transition-all duration-300"
                        onClick={handleSaveUserDetails}
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div >
    );
};

//   Reusable components
const Section = ({ title, children }) => (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 shadow-sm">
        <h3 className="text-base font-semibold mb-3 text-[#0E4C84] border-b pb-1">
            {title}
        </h3>
        {children}
    </div>
);

const InfoItem = ({ label, value }) => (
    <div>
        <p className="text-gray-500 text-xs mb-1">{label}</p>
        <p className="font-medium text-gray-800">{value}</p>
    </div>
);

const Table = ({ headers, data }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-200 rounded-lg">
            <thead className="bg-gray-100">
                <tr>
                    {headers.map((head) => (
                        <th key={head} className="px-3 py-2 text-left font-medium">
                            {head}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((row, idx) => (
                    <tr key={idx} className="border-t">
                        {Object.values(row).map((val, i) => (
                            <td key={i} className="px-3 py-2">
                                {val}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const toTitle = (key) =>
    key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .replace(/_/g, " ");

const ProgressTabs = () => {
    const [activeTab, setActiveTab] = React.useState("financial");

    const financialData = [
        { date: "2025-11-01", completed: "Initial Fund Allocation", progress: "30%", issues: "Delay in invoice clearance" },
        { date: "2025-12-10", completed: "2nd Installment Released", progress: "60%", issues: "None" },
    ];

    const siteData = [
        { date: "2025-11-01", completed: "Base framework setup", progress: "25%", issues: "None" },
        { date: "2025-12-15", completed: "Module integration", progress: "60%", issues: "Minor API delays" },
    ];

    return (
        <div>
            {/* Tabs Header */}
            <div className="flex border-b border-gray-200 mb-4">
                <button
                    onClick={() => setActiveTab("financial")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 ${activeTab === "financial"
                        ? "bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] text-white shadow-md"
                        : "text-gray-600 hover:text-[#0E4C84]"
                        }`}
                >
                    Financial Progress
                </button>
                <button
                    onClick={() => setActiveTab("site")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 ${activeTab === "site"
                        ? "bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] text-white shadow-md"
                        : "text-gray-600 hover:text-[#0E4C84]"
                        }`}
                >
                    Site Progress
                </button>
            </div>

            {/* Tab Content */}
            <div className="mt-2">
                {activeTab === "financial" ? (
                    <Table
                        headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
                        data={financialData}
                    />
                ) : (
                    <Table
                        headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
                        data={siteData}
                    />
                )}
            </div>
        </div>
    );
};

const PaymentFlow = () => {
    const [activeIndex, setActiveIndex] = React.useState(0);

    const paymentCycles = [
        {
            name: "Cycle 1",
            terms: "50% Advance",
            cycle: "Milestone-based",
            summary: "Advance payment on project initiation",
            remarks: "Completed on 2025-10-12",
        },
        {
            name: "Cycle 2",
            terms: "25% Midway",
            cycle: "After module delivery",
            summary: "Second milestone release",
            remarks: "Pending finance approval",
        },
        {
            name: "Cycle 3",
            terms: "25% Final",
            cycle: "After completion & testing",
            summary: "Final payment after client sign-off",
            remarks: "Due March 2026",
        },
    ];

    return (
        <div className="relative w-full max-w-2xl mx-auto">
            {/* Timeline container */}
            <div className="flex flex-col relative before:content-[''] before:absolute before:top-5 before:bottom-5 before:left-[20px] before:w-[2px] before:bg-gray-300">
                {paymentCycles.map((item, index) => (
                    <div
                        key={index}
                        className="flex items-start mb-8 cursor-pointer group"
                        onClick={() => setActiveIndex(index)}
                    >
                        {/* Step indicator */}
                        <div className="relative z-10">
                            <div
                                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all duration-300 
                ${activeIndex === index
                                        ? "bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] text-white border-transparent shadow-lg scale-110"
                                        : "bg-gray-100 border-gray-300 text-gray-700 group-hover:bg-gray-200"
                                    }`}
                            >
                                {index + 1}
                            </div>
                        </div>

                        {/* Step content */}
                        <div className="ml-6 flex-1">
                            <h3
                                className={`font-semibold mb-1 transition-colors duration-300 ${activeIndex === index ? "text-[#0E4C84]" : "text-gray-800"
                                    }`}
                            >
                                {item.name}
                            </h3>
                            <p className="text-xs text-gray-500 mb-2">
                                {item.summary}
                            </p>

                            {/* Show details only for active step */}
                            {activeIndex === index && (
                                <div className="bg-white border border-gray-200 rounded-xl shadow-md p-4 mt-3 text-sm animate-fadeIn">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <InfoItem label="Payment Terms" value={item.terms} />
                                        <InfoItem label="Cycle" value={item.cycle} />
                                        <InfoItem label="Summary" value={item.summary} />
                                        <InfoItem label="Remarks" value={item.remarks} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};



export default ProjectDetailsDialog;


// import React from "react";
// import { FaFileAlt } from "react-icons/fa";

// const ProjectDetailsDialog = ({ isOpen, onClose, setIsAddUpdateDone }) => {
//     if (!isOpen) return null;

//     //   Dummy data for demo
//     const projectInfo = {
//         name: "Technoland Automation",
//         tenderId: "TN-2025-001",
//         createdDate: "2025-10-29",
//         clientName: "ABC Pvt. Ltd.",
//         location: "Mumbai, India",
//         department: "IT & Automation",
//         division: "Technology",
//         objective:
//             "Technoland aims to streamline enterprise operations using AI-driven automation solutions.",
//     };

//     const sow =
//         "The scope of work includes developing a centralized automation dashboard, integrating IoT sensors, and implementing predictive maintenance modules.";

//     const team = [
//         { role: "Project Manager", name: "John Doe", contact: "9876543210", remarks: "Lead PM" },
//         { role: "Site Engineer", name: "Sarah Lee", contact: "9822334455", remarks: "Main site engineer" },
//         { role: "Site Incharge", name: "Raj Patel", contact: "9988776655", remarks: "Overseeing setup" },
//         { role: "Supervisor", name: "Amit Shah", contact: "9877665544", remarks: "Shift supervisor" },
//         { role: "Vendor / Contractor", name: "XYZ Contractors", contact: "9123456789", remarks: "Electrical work" },
//     ];

//     const timeline = {
//         startDate: "2025-10-10",
//         endDate: "2026-03-15",
//         duration: "5 Months",
//         status: "In Progress",
//     };

//     const media = [
//         { type: "Image", channels: "Social Media", duration: "Weekly", remarks: "Promotional posts" },
//         { type: "Video", channels: "YouTube", duration: "Monthly", remarks: "Project updates" },
//     ];

//     const budget = {
//         total: "$500,000",
//         breakdown: "Development: $300K, Marketing: $100K, Logistics: $100K",
//         approvalStatus: "Approved",
//         remarks: "Within allocated budget",
//     };

//     const payment = {
//         terms: "50% Advance, 50% After Completion",
//         cycle: "Milestone-based",
//         summary: "3 payment milestones planned",
//         remarks: "Pending final milestone",
//     };

//     const bank = {
//         name: "HDFC Bank",
//         branch: "Andheri East",
//         accNo: "XXXX-XXXX-1234",
//         ifsc: "HDFC0001234",
//         gst: "27ABCDE1234F1Z9",
//         pan: "ABCDE1234F",
//         method: "NEFT / RTGS",
//         currency: "INR",
//     };

//     const client = {
//         person: "Mr. Ravi Kumar",
//         designation: "Client Manager",
//         email: "ravi.kumar@abc.com",
//         phone: "+91 9876543210",
//         address: "123, Industrial Estate, Mumbai, India",
//     };

//     const progress = [
//         { date: "2025-11-01", completed: "Base framework setup", progress: "25%", issues: "None" },
//         { date: "2025-12-15", completed: "Module integration", progress: "60%", issues: "Minor API delays" },
//     ];

//     const milestones = [
//         { milestone: "Design Phase", expected: "2025-11-30", actual: "2025-12-02", status: "Completed", remarks: "Slight delay" },
//         { milestone: "Development Phase", expected: "2026-02-15", actual: "-", status: "Ongoing", remarks: "On schedule" },
//     ];

//     const notes =
//         "All deliverables to be reviewed weekly. Attachments include design documents, approval letters, and test reports.";

//     const handleSaveUserDetails = async () => {
//         console.log("Project details saved");
//         setIsAddUpdateDone(true);
//         onClose();
//     };

//     return (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
//             <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeInUp flex flex-col text-gray-800">
//                 {/* Header */}
//                 <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
//                     <h2 className="text-lg font-semibold">Project Information</h2>
//                     <button
//                         onClick={onClose}
//                         className="text-gray-500 hover:text-gray-800 transition-all text-lg"
//                     >
//                         ✕
//                     </button>
//                 </div>

//                 {/* Body */}
//                 <div className="p-5 space-y-6 text-sm">
//                     {/* 1. Project Overview */}
//                     <Section title="1. Project Overview">
//                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                             {Object.entries(projectInfo)
//                                 .filter(([k]) => k !== "objective")
//                                 .map(([k, v]) => (
//                                     <InfoItem key={k} label={toTitle(k)} value={v} />
//                                 ))}
//                         </div>
//                         <div className="mt-4">
//                             <p className="text-gray-500 text-xs mb-1">Project Objective / Summary</p>
//                             <p className="font-medium text-gray-700 leading-relaxed">{projectInfo.objective}</p>
//                         </div>
//                     </Section>

//                     {/* 2. Scope of Work */}
//                     <Section title="2. Scope of Work (SOW)">
//                         <p className="text-gray-700 leading-relaxed">{sow}</p>
//                     </Section>

//                     {/* 3. Team & Site Management */}
//                     <Section title="3. Team & Site Management">
//                         <Table
//                             headers={["Role", "Name", "Contact", "Remarks"]}
//                             data={team}
//                         />
//                     </Section>

//                     {/* 4. Timeline */}
//                     <Section title="4. Timeline">
//                         <div className="grid sm:grid-cols-4 gap-4">
//                             {Object.entries(timeline).map(([k, v]) => (
//                                 <InfoItem key={k} label={toTitle(k)} value={v} />
//                             ))}
//                         </div>
//                     </Section>

//                     {/* 5. Media Details */}
//                     <Section title="5. Media Details">
//                         <Table
//                             headers={["Media Type", "Channels", "Duration / Frequency", "Remarks"]}
//                             data={media}
//                         />
//                     </Section>

//                     {/* 6. Budget Information */}
//                     <Section title="6. Budget Information">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             <InfoItem label="Total Budget" value={budget.total} />
//                             <InfoItem label="Cost To Client" value={budget.total} />
//                             <InfoItem label="Cost To Technoland" value={budget.total} />
//                             <InfoItem label="Approval Status" value={budget.approvalStatus} />
//                             <InfoItem label="Cost Breakdown" value={budget.breakdown} />
//                             <InfoItem label="Remarks" value={budget.remarks} />
//                         </div>
//                     </Section>

//                     {/* 7. Payment Cycle & Report */}
//                     <Section title="7. Payment Cycle & Report">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             <InfoItem label="Payment Terms" value={payment.terms} />
//                             <InfoItem label="Cycle" value={payment.cycle} />
//                         </div>
//                         <InfoItem label="Payment Report Summary" value={payment.summary} />
//                         <InfoItem label="Remarks" value={payment.remarks} />
//                     </Section>

//                     {/* 8. Bank & Financial Details */}
//                     <Section title="8. Bank & Financial Details">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             {Object.entries(bank).map(([k, v]) => (
//                                 <InfoItem key={k} label={toTitle(k)} value={v} />
//                             ))}
//                         </div>
//                     </Section>

//                     {/* 9. Client Information */}
//                     <Section title="9. Client Information">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             {Object.entries(client).map(([k, v]) => (
//                                 <InfoItem key={k} label={toTitle(k)} value={v} />
//                             ))}
//                         </div>
//                     </Section>

//                     {/* 10. Progress Summary */}
//                     {/* <Section title="10. Progress Summary">
//                         <Table
//                             headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
//                             data={progress}
//                         />
//                     </Section> */}
//                     {/* 10. Progress Summary with Tabs */}
//                     <Section title="10. Progress Summary">
//                         <ProgressTabs />
//                     </Section>


//                     {/* 11. Milestones & Deliverables */}
//                     <Section title="11. Milestones & Deliverables">
//                         <Table
//                             headers={["Milestone", "Expected Completion", "Actual Completion", "Status", "Remarks"]}
//                             data={milestones}
//                         />
//                     </Section>

//                     {/* 12. Additional Notes / Attachments */}
//                     <Section title="12. Additional Notes / Attachments">
//                         <p className="text-gray-700 leading-relaxed">{notes}</p>
//                     </Section>
//                 </div>

//                 {/* Footer */}
//                 <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 sticky bottom-0">
//                     <button
//                         className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium transition-all"
//                         onClick={onClose}
//                     >
//                         Cancel
//                     </button>
//                     <button
//                         className="bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] hover:from-[#135EA0] hover:to-[#00B8D4] text-white px-5 py-2 rounded-md text-sm font-medium shadow-md transition-all duration-300"
//                         onClick={handleSaveUserDetails}
//                     >
//                         Submit
//                     </button>
//                 </div>
//             </div>
//         </div >
//     );
// };

// // 🧩 Reusable components
// const Section = ({ title, children }) => (
//     <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 shadow-sm">
//         <h3 className="text-base font-semibold mb-3 text-[#0E4C84] border-b pb-1">
//             {title}
//         </h3>
//         {children}
//     </div>
// );

// const InfoItem = ({ label, value }) => (
//     <div>
//         <p className="text-gray-500 text-xs mb-1">{label}</p>
//         <p className="font-medium text-gray-800">{value}</p>
//     </div>
// );

// const Table = ({ headers, data }) => (
//     <div className="overflow-x-auto">
//         <table className="w-full text-sm border border-gray-200 rounded-lg">
//             <thead className="bg-gray-100">
//                 <tr>
//                     {headers.map((head) => (
//                         <th key={head} className="px-3 py-2 text-left font-medium">
//                             {head}
//                         </th>
//                     ))}
//                 </tr>
//             </thead>
//             <tbody>
//                 {data.map((row, idx) => (
//                     <tr key={idx} className="border-t">
//                         {Object.values(row).map((val, i) => (
//                             <td key={i} className="px-3 py-2">
//                                 {val}
//                             </td>
//                         ))}
//                     </tr>
//                 ))}
//             </tbody>
//         </table>
//     </div>
// );

// const toTitle = (key) =>
//     key
//         .replace(/([A-Z])/g, " $1")
//         .replace(/^./, (str) => str.toUpperCase())
//         .replace(/_/g, " ");

// const ProgressTabs = () => {
//     const [activeTab, setActiveTab] = React.useState("financial");

//     const financialData = [
//         { date: "2025-11-01", completed: "Initial Fund Allocation", progress: "30%", issues: "Delay in invoice clearance" },
//         { date: "2025-12-10", completed: "2nd Installment Released", progress: "60%", issues: "None" },
//     ];

//     const siteData = [
//         { date: "2025-11-01", completed: "Base framework setup", progress: "25%", issues: "None" },
//         { date: "2025-12-15", completed: "Module integration", progress: "60%", issues: "Minor API delays" },
//     ];

//     return (
//         <div>
//             {/* Tabs Header */}
//             <div className="flex border-b border-gray-200 mb-4">
//                 <button
//                     onClick={() => setActiveTab("financial")}
//                     className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 ${activeTab === "financial"
//                         ? "bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] text-white shadow-md"
//                         : "text-gray-600 hover:text-[#0E4C84]"
//                         }`}
//                 >
//                     Financial Progress
//                 </button>
//                 <button
//                     onClick={() => setActiveTab("site")}
//                     className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 ${activeTab === "site"
//                         ? "bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] text-white shadow-md"
//                         : "text-gray-600 hover:text-[#0E4C84]"
//                         }`}
//                 >
//                     Site Progress
//                 </button>
//             </div>

//             {/* Tab Content */}
//             <div className="mt-2">
//                 {activeTab === "financial" ? (
//                     <Table
//                         headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
//                         data={financialData}
//                     />
//                 ) : (
//                     <Table
//                         headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
//                         data={siteData}
//                     />
//                 )}
//             </div>
//         </div>
//     );
// };

// export default ProjectDetailsDialog;





// import React from "react";
// import { FaFileAlt } from "react-icons/fa";

// const ProjectDetailsDialog = ({ isOpen, onClose, setIsAddUpdateDone }) => {
//     if (!isOpen) return null;

//     //   Dummy data for demo
//     const projectInfo = {
//         name: "Technoland Automation",
//         tenderId: "TN-2025-001",
//         createdDate: "2025-10-29",
//         clientName: "ABC Pvt. Ltd.",
//         location: "Mumbai, India",
//         department: "IT & Automation",
//         division: "Technology",
//         objective:
//             "Technoland aims to streamline enterprise operations using AI-driven automation solutions.",
//     };

//     const sow =
//         "The scope of work includes developing a centralized automation dashboard, integrating IoT sensors, and implementing predictive maintenance modules.";

//     const team = [
//         { role: "Project Manager", name: "John Doe", contact: "9876543210", remarks: "Lead PM" },
//         { role: "Site Engineer", name: "Sarah Lee", contact: "9822334455", remarks: "Main site engineer" },
//         { role: "Site Incharge", name: "Raj Patel", contact: "9988776655", remarks: "Overseeing setup" },
//         { role: "Supervisor", name: "Amit Shah", contact: "9877665544", remarks: "Shift supervisor" },
//         { role: "Vendor / Contractor", name: "XYZ Contractors", contact: "9123456789", remarks: "Electrical work" },
//     ];

//     const timeline = {
//         startDate: "2025-10-10",
//         endDate: "2026-03-15",
//         duration: "5 Months",
//         status: "In Progress",
//     };

//     const media = [
//         { type: "Image", channels: "Social Media", duration: "Weekly", remarks: "Promotional posts" },
//         { type: "Video", channels: "YouTube", duration: "Monthly", remarks: "Project updates" },
//     ];

//     const budget = {
//         total: "$500,000",
//         breakdown: "Development: $300K, Marketing: $100K, Logistics: $100K",
//         approvalStatus: "Approved",
//         remarks: "Within allocated budget",
//     };

//     const payment = {
//         terms: "50% Advance, 50% After Completion",
//         cycle: "Milestone-based",
//         summary: "3 payment milestones planned",
//         remarks: "Pending final milestone",
//     };

//     const bank = {
//         name: "HDFC Bank",
//         branch: "Andheri East",
//         accNo: "XXXX-XXXX-1234",
//         ifsc: "HDFC0001234",
//         gst: "27ABCDE1234F1Z9",
//         pan: "ABCDE1234F",
//         method: "NEFT / RTGS",
//         currency: "INR",
//     };

//     const client = {
//         person: "Mr. Ravi Kumar",
//         designation: "Client Manager",
//         email: "ravi.kumar@abc.com",
//         phone: "+91 9876543210",
//         address: "123, Industrial Estate, Mumbai, India",
//     };

//     const progress = [
//         { date: "2025-11-01", completed: "Base framework setup", progress: "25%", issues: "None" },
//         { date: "2025-12-15", completed: "Module integration", progress: "60%", issues: "Minor API delays" },
//     ];

//     const milestones = [
//         { milestone: "Design Phase", expected: "2025-11-30", actual: "2025-12-02", status: "Completed", remarks: "Slight delay" },
//         { milestone: "Development Phase", expected: "2026-02-15", actual: "-", status: "Ongoing", remarks: "On schedule" },
//     ];

//     const notes =
//         "All deliverables to be reviewed weekly. Attachments include design documents, approval letters, and test reports.";

//     const handleSaveUserDetails = async () => {
//         console.log("Project details saved");
//         setIsAddUpdateDone(true);
//         onClose();
//     };

//     return (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
//             <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeInUp flex flex-col text-gray-800">
//                 {/* Header */}
//                 <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
//                     <h2 className="text-lg font-semibold">Project Information</h2>
//                     <button
//                         onClick={onClose}
//                         className="text-gray-500 hover:text-gray-800 transition-all text-lg"
//                     >
//                         ✕
//                     </button>
//                 </div>

//                 {/* Body */}
//                 <div className="p-5 space-y-6 text-sm">
//                     {/* 1. Project Overview */}
//                     <Section title="1. Project Overview">
//                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                             {Object.entries(projectInfo)
//                                 .filter(([k]) => k !== "objective")
//                                 .map(([k, v]) => (
//                                     <InfoItem key={k} label={toTitle(k)} value={v} />
//                                 ))}
//                         </div>
//                         <div className="mt-4">
//                             <p className="text-gray-500 text-xs mb-1">Project Objective / Summary</p>
//                             <p className="font-medium text-gray-700 leading-relaxed">{projectInfo.objective}</p>
//                         </div>
//                     </Section>

//                     {/* 2. Scope of Work */}
//                     <Section title="2. Scope of Work (SOW)">
//                         <p className="text-gray-700 leading-relaxed">{sow}</p>
//                     </Section>

//                     {/* 3. Team & Site Management */}
//                     <Section title="3. Team & Site Management">
//                         <Table
//                             headers={["Role", "Name", "Contact", "Remarks"]}
//                             data={team}
//                         />
//                     </Section>

//                     {/* 4. Timeline */}
//                     <Section title="4. Timeline">
//                         <div className="grid sm:grid-cols-4 gap-4">
//                             {Object.entries(timeline).map(([k, v]) => (
//                                 <InfoItem key={k} label={toTitle(k)} value={v} />
//                             ))}
//                         </div>
//                     </Section>

//                     {/* 5. Media Details */}
//                     <Section title="5. Media Details">
//                         <Table
//                             headers={["Media Type", "Channels", "Duration / Frequency", "Remarks"]}
//                             data={media}
//                         />
//                     </Section>

//                     {/* 6. Budget Information */}
//                     <Section title="6. Budget Information">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             <InfoItem label="Total Budget" value={budget.total} />
//                             <InfoItem label="Approval Status" value={budget.approvalStatus} />
//                         </div>
//                         <InfoItem label="Cost Breakdown" value={budget.breakdown} />
//                         <InfoItem label="Remarks" value={budget.remarks} />
//                     </Section>

//                     {/* 7. Payment Cycle & Report */}
//                     <Section title="7. Payment Cycle & Report">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             <InfoItem label="Payment Terms" value={payment.terms} />
//                             <InfoItem label="Cycle" value={payment.cycle} />
//                         </div>
//                         <InfoItem label="Payment Report Summary" value={payment.summary} />
//                         <InfoItem label="Remarks" value={payment.remarks} />
//                     </Section>

//                     {/* 8. Bank & Financial Details */}
//                     <Section title="8. Bank & Financial Details">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             {Object.entries(bank).map(([k, v]) => (
//                                 <InfoItem key={k} label={toTitle(k)} value={v} />
//                             ))}
//                         </div>
//                     </Section>

//                     {/* 9. Client Information */}
//                     <Section title="9. Client Information">
//                         <div className="grid sm:grid-cols-2 gap-4">
//                             {Object.entries(client).map(([k, v]) => (
//                                 <InfoItem key={k} label={toTitle(k)} value={v} />
//                             ))}
//                         </div>
//                     </Section>

//                     {/* 10. Progress Summary */}
//                     <Section title="10. Progress Summary">
//                         <Table
//                             headers={["Date", "Work Completed", "% Progress", "Issues / Delays"]}
//                             data={progress}
//                         />
//                     </Section>

//                     {/* 11. Milestones & Deliverables */}
//                     <Section title="11. Milestones & Deliverables">
//                         <Table
//                             headers={["Milestone", "Expected Completion", "Actual Completion", "Status", "Remarks"]}
//                             data={milestones}
//                         />
//                     </Section>

//                     {/* 12. Additional Notes / Attachments */}
//                     <Section title="12. Additional Notes / Attachments">
//                         <p className="text-gray-700 leading-relaxed">{notes}</p>
//                     </Section>
//                 </div>

//                 {/* Footer */}
//                 <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 sticky bottom-0">
//                     <button
//                         className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium transition-all"
//                         onClick={onClose}
//                     >
//                         Cancel
//                     </button>
//                     <button
//                         className="bg-gradient-to-r from-[#0E4C84] to-[#00A7C1] hover:from-[#135EA0] hover:to-[#00B8D4] text-white px-5 py-2 rounded-md text-sm font-medium shadow-md transition-all duration-300"
//                         onClick={handleSaveUserDetails}
//                     >
//                         Submit
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );
// };

// // 🧩 Reusable components
// const Section = ({ title, children }) => (
//     <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 shadow-sm">
//         <h3 className="text-base font-semibold mb-3 text-[#0E4C84] border-b pb-1">
//             {title}
//         </h3>
//         {children}
//     </div>
// );

// const InfoItem = ({ label, value }) => (
//     <div>
//         <p className="text-gray-500 text-xs mb-1">{label}</p>
//         <p className="font-medium text-gray-800">{value}</p>
//     </div>
// );

// const Table = ({ headers, data }) => (
//     <div className="overflow-x-auto">
//         <table className="w-full text-sm border border-gray-200 rounded-lg">
//             <thead className="bg-gray-100">
//                 <tr>
//                     {headers.map((head) => (
//                         <th key={head} className="px-3 py-2 text-left font-medium">
//                             {head}
//                         </th>
//                     ))}
//                 </tr>
//             </thead>
//             <tbody>
//                 {data.map((row, idx) => (
//                     <tr key={idx} className="border-t">
//                         {Object.values(row).map((val, i) => (
//                             <td key={i} className="px-3 py-2">
//                                 {val}
//                             </td>
//                         ))}
//                     </tr>
//                 ))}
//             </tbody>
//         </table>
//     </div>
// );

// const toTitle = (key) =>
//     key
//         .replace(/([A-Z])/g, " $1")
//         .replace(/^./, (str) => str.toUpperCase())
//         .replace(/_/g, " ");

// export default ProjectDetailsDialog;


